import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../../shared/components/Icon';
import FilterPopup from '../../../../shared/components/modals/FilterPopup';
import { useOverviewFilters } from '../../../../shared/context/OverviewFilterContext';
import axiosInstance from '../../../../shared/services/api/axiosInstance';
import OnchainMetricsCards from './components/OnchainMetricsCards';
import WalletAgeDistributionChart from './components/WalletAgeDistributionChart';
import TransactionVolumeCards from './components/TransactionVolumeCards';
import TransactionActivityChart from './components/TransactionActivityChart';
import WalletBalanceDistributionChart from './components/WalletBalanceDistributionChart';
import { getDefaultDateRange } from '../../../../shared/utils/dateUtils';

const Onchain = () => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { appliedFilters, updateFilters, hasActiveFilters, teamId } = useOverviewFilters();
  const [metrics, setMetrics] = useState(null);
  const [transactionActivityData, setTransactionActivityData] = useState([]);
  const [transactionActivityTimeRange, setTransactionActivityTimeRange] = useState('monthly');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasInitializedFilters, setHasInitializedFilters] = useState(false);

  // Initialize default filters (first contract) when Onchain mounts
  useEffect(() => {
    const initializeDefaultFilters = async () => {
      if (!teamId || hasInitializedFilters) return;
      
      // Only initialize if no contract filter is currently set
      const hasContracts = appliedFilters.contracts && appliedFilters.contracts.length > 0;
      
      if (hasContracts) {
        setHasInitializedFilters(true);
        return;
      }

      try {
        // Fetch contracts
        let firstContract = null;
        try {
          let teamName = teamId;
          if (typeof teamId === 'number' || !isNaN(teamId)) {
            teamName = localStorage.getItem('selectedTeam') || teamId;
          }
          
          const contractsResponse = await axiosInstance.get(`/smart-contracts/team/${encodeURIComponent(teamName)}`);
          
          if (contractsResponse.data?.contracts?.length > 0) {
            const contract = contractsResponse.data.contracts[0];
            firstContract = {
              id: contract.contractId || contract.id,
              address: contract.address || contract.contract_address,
              name: contract.name || contract.contract_name || `Contract ${(contract.address || contract.contract_address || '').substring(0, 6)}...${(contract.address || contract.contract_address || '').substring((contract.address || contract.contract_address || '').length - 4)}`,
              blockchain: contract.blockchain || contract.chain,
              tokenSymbol: contract.tokenSymbol || contract.symbol || contract.contract_symbol,
              contractType: contract.contractType || 'main'
            };
          }
        } catch (err) {
          console.warn('Error fetching contracts for default filter:', err);
        }

        // Update filters with first contract
        if (firstContract) {
          updateFilters({
            websites: appliedFilters.websites || [],
            contracts: [firstContract],
            sessions: appliedFilters.sessions || [],
            userIdentities: appliedFilters.userIdentities || [],
            dateRange: appliedFilters.dateRange || getDefaultDateRange()
          });
        }
        setHasInitializedFilters(true);
      } catch (error) {
        console.error('Error initializing default filters:', error);
        setHasInitializedFilters(true); // Set to true even on error to prevent infinite loops
      }
    };

    initializeDefaultFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, hasInitializedFilters]);

  // Reset initialization flag when team changes
  useEffect(() => {
    setHasInitializedFilters(false);
  }, [teamId]);

  const handleFiltersChange = (filters) => {
    updateFilters(filters);
  };

  // Fetch onchain metrics
  const fetchOnchainMetrics = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      if (!teamId) {
        setMetrics({
          totalUniqueWallets: 0,
          activeWalletsLast30Days: 0,
          totalTransactionCount: 0,
          transactionCountLast30Days: 0,
          transactionCountLast7Days: 0,
          volumeLast7Days: 0,
          volumeLast30Days: 0,
          volumeLastYear: 0,
          volumeLifetime: 0,
          avgVolume: 0,
          medianVolume: 0
        });
        return;
      }

      const params = { team_id: teamId };

      // Add contract filter if selected
      if (appliedFilters.contracts && appliedFilters.contracts.length > 0) {
        params.contracts = appliedFilters.contracts.map(c => c.id || c).join(',');
      }

      const response = await axiosInstance.get('/overview/onchain-metrics', { params });

      if (response.data?.success) {
        setMetrics(response.data.metrics);
      } else {
        console.warn('Onchain metrics response not successful:', response.data);
        setMetrics({
          totalUniqueWallets: 0,
          activeWalletsLast30Days: 0,
          totalTransactionCount: 0,
          transactionCountLast30Days: 0,
          transactionCountLast7Days: 0,
          volumeLast7Days: 0,
          volumeLast30Days: 0,
          volumeLastYear: 0,
          volumeLifetime: 0,
          avgVolume: 0,
          medianVolume: 0
        });
      }
    } catch (error) {
      console.error('Error fetching onchain metrics:', error);
      setError(error.message);
      setMetrics({
        totalUniqueWallets: 0,
        activeWalletsLast30Days: 0,
        totalTransactionCount: 0,
        transactionCountLast30Days: 0,
        transactionCountLast7Days: 0,
        volumeLast7Days: 0,
        volumeLast30Days: 0,
        volumeLastYear: 0,
        volumeLifetime: 0
      });
    } finally {
      setIsLoading(false);
    }
  }, [appliedFilters.contracts, teamId]);

  // Fetch transaction activity chart
  const fetchTransactionActivityChart = useCallback(async () => {
    try {
      if (!teamId) {
        setTransactionActivityData([]);
        return;
      }

      const params = {
        team_id: teamId,
        timeRange: transactionActivityTimeRange
      };

      // Add contract filter if selected
      if (appliedFilters.contracts && appliedFilters.contracts.length > 0) {
        params.contracts = appliedFilters.contracts.map(c => c.id || c).join(',');
      }

      const response = await axiosInstance.get('/overview/transaction-activity-chart', { params });

      if (response.data?.data) {
        setTransactionActivityData(response.data.data);
      } else {
        console.warn('Transaction activity chart response not successful:', response.data);
        setTransactionActivityData([]);
      }
    } catch (error) {
      console.error('Error fetching transaction activity chart:', error);
      setTransactionActivityData([]);
    }
  }, [teamId, appliedFilters.contracts, transactionActivityTimeRange]);

  useEffect(() => {
    fetchOnchainMetrics();
  }, [fetchOnchainMetrics]);

  useEffect(() => {
    fetchTransactionActivityChart();
  }, [fetchTransactionActivityChart]);

  const styles = {
    primaryBg: "#ffffff",
    secondaryBg: "#DDE2EE",
    textPrimary: "#0f172a",
    textSecondary: "#666666",
    accentColor: "#1d0c46",
    borderColor: "#DDE2EE"
  };

  return (
    <div className="relative h-full w-full overflow-y-auto">
      {/* Filter Button - Vertical, fixed to right edge */}
      <button
        onClick={() => setIsFilterOpen(true)}
        className="fixed z-10 transition-colors shadow-sm flex items-center justify-center gap-2 bg-white/80 backdrop-blur-sm hover:bg-white text-[#475569]"
        style={{ 
          fontFamily: 'Raleway, sans-serif',
          right: 0,
          top: '16px',
          padding: '16px 8px',
          writingMode: 'vertical-rl',
          textOrientation: 'mixed',
          transform: 'none',
          border: '1px solid #DDE2EE',
          borderRight: 'none',
          borderRadius: 0
        }}
      >
        <Icon name="filter" size={18} style={{ transform: 'rotate(90deg)' }} />
        <span className="text-sm font-medium">Filters</span>
        {hasActiveFilters() && (
          <span className="text-xs opacity-90">•</span>
        )}
      </button>

      {/* Page Content */}
      <div className="p-4 md:p-6 lg:p-8 pr-20">
        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded">
            Error: {error}
          </div>
        )}

        {/* Metrics Cards */}
        <OnchainMetricsCards data={metrics} isLoading={isLoading} />

        {/* Wallet Age Distribution and Transaction Volume */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          {/* Wallet Age Distribution Chart */}
          <WalletAgeDistributionChart 
            teamId={teamId} 
            appliedFilters={appliedFilters}
            isLoading={isLoading}
          />

          {/* Transaction Volume Cards */}
          <TransactionVolumeCards 
            data={metrics}
            isLoading={isLoading}
          />
        </div>

        {/* Transaction Activity Over Time Chart */}
        <div className="mt-4" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
          <div className="p-4 md:p-6 flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium" style={{ color: styles.accentColor, fontFamily: 'Raleway, sans-serif' }}>
              Transaction Activity Over Time
            </h3>
            {/* Time Range Toggle */}
            <div className="flex border overflow-hidden" style={{ borderColor: styles.borderColor }}>
              {['daily', 'weekly', 'monthly', 'yearly'].map((range) => (
                <button
                  key={range}
                  onClick={() => setTransactionActivityTimeRange(range)}
                  className={`px-3 py-1 text-xs transition-colors ${
                    transactionActivityTimeRange === range
                      ? 'text-white'
                      : 'bg-white hover:bg-gray-50'
                  }`}
                  style={{
                    fontFamily: 'Raleway, sans-serif',
                    backgroundColor: transactionActivityTimeRange === range ? styles.accentColor : styles.primaryBg,
                    color: transactionActivityTimeRange === range ? '#ffffff' : styles.textPrimary
                  }}
                >
                  {range.charAt(0).toUpperCase() + range.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div className="h-80 w-full">
            <TransactionActivityChart data={transactionActivityData} timeRange={transactionActivityTimeRange} isLoading={isLoading} />
          </div>
        </div>

        {/* Wallet Balance Distribution Chart */}
        <div className="mt-4">
          <WalletBalanceDistributionChart 
            teamId={teamId} 
            appliedFilters={appliedFilters}
            isLoading={isLoading}
          />
        </div>
      </div>

      {/* Filter Popup */}
      <FilterPopup 
        isOpen={isFilterOpen} 
        onClose={() => setIsFilterOpen(false)}
        onFiltersChange={handleFiltersChange}
        appliedFilters={appliedFilters}
        teamId={localStorage.getItem('selectedTeam') || 'Cryptique'}
      />
    </div>
  );
};

export default Onchain;

